import { type CSSProperties, useEffect, useRef, useState } from 'react';

import { useResizeDetector } from 'react-resize-detector';
import * as styles from '../navbarElement/Navbar.css';
import type { NavbarMenuListGroupElementProps } from '../navbarElement/Navbar.type';
import { cn } from '../utils/cn';
import { getHeightStyle } from '../utils/sizing';

const DEFAULTS = {
  minHeight: '28px',
};

export function NavbarMenuListGroupV2(props: NavbarMenuListGroupElementProps) {
  const { element, className, children, style, screen, attributes } = props;
  const { type, customAttributes } = element.attrs || {};

  const middleOuterRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);

  const { height: middleWrapperHeight, ref: middleWrapperRef } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 100,
  });

  // Observe content width changes
  useEffect(() => {
    if (!middleOuterRef.current || type !== 'middle') return;
    const leftSibling = middleOuterRef.current?.parentElement?.firstChild as HTMLDivElement;
    const rightSibling = middleOuterRef.current?.parentElement?.lastChild as HTMLDivElement;
    const parentElement = middleOuterRef.current?.parentElement;

    const updateMaxWidth = () => {
      const biggestSiblingWidth = Math.max(leftSibling?.scrollWidth || 0, rightSibling?.scrollWidth || 0);
      const parentWidth = parentElement?.scrollWidth || 0;
      setMaxWidth(parentWidth - biggestSiblingWidth * 2);
    };

    // Create observers for left and right siblings
    const resizeObserver = new ResizeObserver(updateMaxWidth);
    if (leftSibling) resizeObserver.observe(leftSibling);
    if (rightSibling) resizeObserver.observe(rightSibling);
    if (parentElement) resizeObserver.observe(parentElement);

    // Initial max width calculation
    updateMaxWidth();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (type === 'left') {
    return (
      <div
        {...props}
        {...attributes}
        {...customAttributes}
        className={cn(styles.navbarMenuListGroup, styles.alignLeft, className)}
        style={
          {
            '--min-height': middleWrapperHeight ? getHeightStyle(middleWrapperHeight).height : DEFAULTS.minHeight,
            ...style,
          } as CSSProperties
        }
      >
        {children}
      </div>
    );
  }

  if (type === 'middle') {
    return (
      <>
        {/* This centerPlaceholder is required to make sure the middle doesn't collapse to 0 width when width is `fit-content` */}
        {screen !== 'mobile' && (
          <div
            className={styles.centerPlaceholder}
            style={{ '--center-placeholder-width': maxWidth ? `${maxWidth}px` : '100%' } as CSSProperties}
          />
        )}
        <div
          ref={middleOuterRef}
          {...attributes}
          className={cn(styles.centerWrapper)}
          style={{ ...style, '--max-width': maxWidth ? `${maxWidth}px` : '100%', top: 0 } as CSSProperties}
        >
          <div
            {...props}
            {...customAttributes}
            className={cn(styles.navbarMenuListGroup, styles.alignCenter, className)}
            ref={middleWrapperRef}
          >
            {children}
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      {...props}
      {...attributes}
      {...customAttributes}
      className={cn(styles.navbarMenuListGroup, styles.alignRight, className)}
      style={
        {
          '--min-height': middleWrapperHeight ? getHeightStyle(middleWrapperHeight).height : DEFAULTS.minHeight,
          ...style,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
}
